import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Chart } from 'chart.js';
import { replaceRoute } from '../../lib/router.js';
import {
  PROJECTS_INDEX,
  PROJECTS_INITIATIVES,
} from '../../constants/routes.js';
import { useFetchFile } from '../../hooks/useFetchFile.js';
import Empty from '../../components/Empty.jsx';
import { loadingContent } from '../../components/EmptyStates.jsx';
import {
  selectCurrentOrganizationId,
  selectUser,
  selectUserPermissions,
} from '../../modules/auth.js';
import {
  deleteProject,
  retrieveIsShowAlertOnDelete,
  retrieveProject,
  selectProject,
  selectRetrieveProjectState,
} from '../../modules/projects.js';
import {
  retrieveChangeStrategy,
  selectChangeStrategy,
} from '../../modules/changeStrategy.js';
import {
  retrieveProjectStatuses,
  selectProjectStatusesWithArchived,
  selectRetrieveStatusesState,
} from '../../modules/projectStatuses.js';
import {
  retrievePortfolio,
  retrievePortfolios,
  selectPortfolio,
} from '../../modules/portfolio.js';
import { deleteInitiative } from '../../modules/initiatives.js';
import {
  retrieveProjectDashboard,
  searchPeopleEngagement,
  selectPeopleEngagementData,
  selectPeopleEngagementQuery,
  selectPeopleEngagementSearchState,
  selectProjectDashboard,
  selectRetrieveDashboardState,
  updatePeopleEngagementQuery,
} from '../../modules/projectDashboard.js';
import ProjectHeader from '../../components/headers/PageHeaders/Project/ProjectHeader.jsx';
import SubHeader from '@/src/components/SubHeader.jsx';
import { Button, Col, Row } from 'react-bootstrap';
import { DownloadWhiteIcon } from '@/src/components/icons/DownloadWhiteIcon.jsx';
import { ProjectOverviewWidget } from '@/src/components/projects/ProjectDashboard/ProjectOverviewWidget.jsx';
import { selectOrganization } from '@/src/modules/organizations.js';
import { InitiativesWidget } from '@/src/components/projects/ProjectDashboard/InitiativesWidget.jsx';
import { InitiativeStatusesWidget } from '@/src/components/projects/ProjectDashboard/InitiativeStatusesWidget.jsx';
import { ImpactedPeopleWidget } from '@/src/components/projects/ProjectDashboard/ImpactedPeopleWidget.jsx';
import { CITasksWidget } from '@/src/components/projects/ProjectDashboard/CITasksWidget.jsx';
import { StakeholdersWidget } from '@/src/components/projects/ProjectDashboard/StakeholdersWidget.jsx';
import { StakeholderSupportWidget } from '@/src/components/projects/ProjectDashboard/StakeholderSupportWidget.jsx';
import { MilestonesWidget } from '@/src/components/projects/ProjectDashboard/MilestonesWidget.jsx';
import { FeedbackIntelligenceWidget } from '@/src/components/projects/ProjectDashboard/FeedbackIIntelligenceWidget.jsx';
import { PeopleEngagementWidget } from '@/src/components/projects/ProjectDashboard/PeopleEngagementWidget.jsx';
import ProjectActionDropdown from '@/src/components/projects/ProjectActionDropdown.jsx';
import { getOrgTermsContent } from '@/src/content/utils.js';
import { DeleteModal } from '@/src/components/modals/DeleteModal.jsx';
import { isAuthorized } from '@/src/components/AuthorizedAction.js';

const mapStateToProps = (state, ownProps) => {
  const retrieveSuccess = [
    selectRetrieveProjectState,
    selectRetrieveStatusesState,
    selectRetrieveDashboardState(ownProps.projectId),
  ].every((select) => select(state).success);

  const project = selectProject(ownProps.projectId)(state);
  const organization = selectOrganization(state);

  return {
    retrieveSuccess,
    project,
    changeStrategy: selectChangeStrategy(state),
    projectStatuses: selectProjectStatusesWithArchived(state),
    projectDashboard: selectProjectDashboard(ownProps.projectId)(state),
    peopleEngagementData: selectPeopleEngagementData(ownProps.projectId)(state),
    peopleEngagementQuery: selectPeopleEngagementQuery(ownProps.projectId)(
      state
    ),
    peopleEngagementSearchState: selectPeopleEngagementSearchState(
      ownProps.projectId
    )(state),
    portfolio: project?.portfolioId
      ? selectPortfolio(project.portfolioId)(state)
      : null,
    user: selectUser(state),
    permissions: selectUserPermissions(state),
    organization,
    apiUrl: state.config.apiUrl,
    organizationId: selectCurrentOrganizationId(state),
  };
};
const mapDispatchToProps = {
  retrieveProject,
  retrieveChangeStrategy,
  retrieveProjectDashboard,
  searchPeopleEngagement,
  updatePeopleEngagementQuery,
  deleteInitiative,
  retrieveProjectStatuses,
  retrievePortfolio,
  retrievePortfolios,
  deleteProject,
  retrieveIsShowAlertOnDelete,
};

function ProjectDashboardContainer({
  project,
  projectId,
  retrieveProject,
  permissions,
  retrievePortfolios,
  apiUrl,
  organizationId,
  retrieveSuccess,
  retrieveChangeStrategy,
  retrieveProjectStatuses,
  retrieveProjectDashboard,
  searchPeopleEngagement,
  updatePeopleEngagementQuery,
  projectDashboard,
  peopleEngagementData,
  peopleEngagementQuery,
  peopleEngagementSearchState,
  organization,
  deleteProject,
  retrieveIsShowAlertOnDelete,
}) {
  const { downloadBlob } = useFetchFile();
  const [loading, setLoading] = useState(false);
  const [deleteProjectState, setDeleteProjectState] = useState({
    showModal: false,
    projectId: null,
  });

  const onConfirmDeleteProject = (projectId) => {
    deleteProject(projectId || deleteProjectState.projectId).then(() => {
      setDeleteProjectState({ showModal: false, projectId: null });
      replaceRoute(PROJECTS_INDEX);
    });
  };

  const onDeleteProject = (projectId) => {
    retrieveIsShowAlertOnDelete(projectId).then(({ showAlert }) => {
      setDeleteProjectState({ showModal: showAlert, projectId });
      if (!showAlert) {
        window.confirm(
          `${getOrgTermsContent('deleteProject')} \n\n${getOrgTermsContent('deleteProjectDescription')} `
        ) && onConfirmDeleteProject(projectId);
      }
    });
  };

  const handlePeopleEngagementSearch = (e) => {
    const query = e.target.value;
    updatePeopleEngagementQuery(projectId, query);
    searchPeopleEngagement(projectId, query);
  };

  const extractSingleChartPNG = (canvas, fileName) => {
    return new Promise((resolve) => {
      const chart = Chart.getChart(canvas);
      if (!chart) {
        console.error('Chart instance not found for:', fileName);
        resolve(null);
        return;
      }

      // Static dimensions config per chart type
      const CHART_EXPORT_DIMENSIONS = {
        impacted_people: { width: 555, height: 300 }, // Doughnut - square
        initiative_statuses: { width: 555, height: 300 }, // Bar chart - wider
        stakeholder_support: { width: 555, height: 300 }, // Bar chart - wider
        change_impact_tasks: { width: 555, height: 300 }, // Doughnut - square
      };

      const dimensions = CHART_EXPORT_DIMENSIONS[fileName] || {
        width: 1000,
        height: 800,
      };
      const exportWidth = dimensions.width;
      const exportHeight = dimensions.height;

      const offscreenCanvas = document.createElement('canvas');
      const container = document.createElement('div');
      container.style.position = 'absolute';
      container.style.left = '-9999px';
      container.style.width = `${exportWidth}px`;
      container.style.height = `${exportHeight}px`;
      container.appendChild(offscreenCanvas);
      document.body.appendChild(container);

      let renderComplete = false;
      let tempChart = null;

      const cleanup = () => {
        try {
          if (tempChart) {
            tempChart.destroy();
          }
          if (container.parentNode) {
            document.body.removeChild(container);
          }
        } catch (error) {
          console.error('Cleanup error for chart:', fileName, error);
        }
      };

      const extractImage = () => {
        if (renderComplete) return;
        renderComplete = true;

        try {
          offscreenCanvas.toBlob(
            (blob) => {
              cleanup();
              if (blob) {
                resolve({ fileName, blob });
              } else {
                resolve(null);
              }
            },
            'image/png',
            1
          );
        } catch (error) {
          cleanup();
          resolve(null);
        }
      };

      // Use original chart config, only override specific options
      const highResConfig = {
        type: chart.config.type,
        data: chart.config.data,
        options: {
          ...chart.config.options,
          animation: {
            duration: 0,
            onComplete: extractImage,
          },
          devicePixelRatio: 2,
        },
      };

      try {
        tempChart = new Chart(offscreenCanvas, highResConfig);

        setTimeout(() => {
          if (!renderComplete) {
            extractImage();
          }
        }, 2000);
      } catch (error) {
        cleanup();
        resolve(null);
      }
    });
  };

  const onDownloadDashboardPdf = async () => {
    setLoading(true);

    try {
      const chartConfigs = [
        { selector: '.impacted-people-widget canvas', key: 'impacted_people' },
        {
          selector: '.initiatives-statuses-widget canvas',
          key: 'initiative_statuses',
        },
        {
          selector: '.stakeholder-support-widget canvas',
          key: 'stakeholder_support',
        },
        { selector: '.ci-tasks-widget canvas', key: 'change_impact_tasks' },
      ];

      const extractionPromises = chartConfigs.map(({ selector, key }) => {
        const canvas = document.querySelector(selector);
        if (canvas) {
          return extractSingleChartPNG(canvas, key).then((result) =>
            result ? { key, blob: result.blob } : null
          );
        }
        return Promise.resolve(null);
      });

      const results = await Promise.all(extractionPromises);

      const formData = new FormData();
      results.forEach((result) => {
        if (result && result.blob) {
          formData.append(result.key, result.blob, `${result.key}.png`);
        }
      });

      await downloadBlob(
        apiUrl,
        `organizations/${organizationId}/projects/${projectId}/dashboard/pdf`,
        {},
        `${project.name}-dashboard`,
        'pdf',
        'POST',
        formData
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    retrieveProjectStatuses();
    retrievePortfolios();
  }, []);

  useEffect(() => {
    const loadDashboard = async () => {
      if (project?.dashboardPage === false) {
        replaceRoute(PROJECTS_INITIATIVES, { projectId });
        return;
      }

      const fetchedProject = await retrieveProject(projectId);

      if (fetchedProject?.dashboardPage === false) {
        replaceRoute(PROJECTS_INITIATIVES, { projectId });
        return;
      }

      if (
        fetchedProject?.dashboardPage === true ||
        fetchedProject?.dashboardPage === undefined
      ) {
        Promise.all([
          retrieveChangeStrategy(projectId),
          retrieveProjectDashboard(projectId),
          searchPeopleEngagement(projectId),
        ]);
      }
    };

    loadDashboard();
  }, [projectId]);

  if (project?.dashboardPage === false) {
    return null;
  }

  if (!retrieveSuccess) {
    return <Empty simplified content={loadingContent} />;
  }

  const {
    projectOverview,
    initiatives,
    initiativeStatuses,
    impactedPeople,
    changeImpactTasks,
    projectMilestones,
    feedbackIntelligence,
    stakeholdersOverview,
    stakeholderSupport,
  } = projectDashboard;

  return (
    <>
      <ProjectHeader project={project} />
      <SubHeader flex flexJustify small>
        <h2 style={{ minWidth: '350px' }}>Dashboard</h2>
        <div className="flex">
          <Button
            bsStyle="primary"
            className="flex"
            onClick={onDownloadDashboardPdf}
            disabled={loading}
          >
            <span className="flex m-r-8">
              <DownloadWhiteIcon />
            </span>
            {loading ? 'Loading...' : 'Download PDF'}
          </Button>
          {((isAuthorized('destroy', 'project', permissions) &&
            project.manageProject) ||
            project.canDuplicate) && (
            <ProjectActionDropdown
              className=" m-l-16"
              project={project}
              projectId={projectId}
              onDeleteProject={onDeleteProject}
            />
          )}
        </div>
      </SubHeader>

      {!retrieveSuccess ? (
        <Empty simplified content={loadingContent} />
      ) : (
        <div className="component component-hide-border m-b-38">
          <Row className="m-b-38">
            <Col xs={12}>
              <ProjectOverviewWidget
                data={{
                  ...projectOverview,
                  ...project,
                  hasParentPortfolios: organization.hasParentPortfolios,
                }}
              />
            </Col>
          </Row>
          <Row className="m-b-38">
            <Col xs={6}>
              <InitiativesWidget data={initiatives} />
            </Col>
            <Col xs={6}>
              <InitiativeStatusesWidget data={initiativeStatuses} />
            </Col>
          </Row>

          <Row className="m-b-38">
            <Col xs={12}>
              <PeopleEngagementWidget
                data={peopleEngagementData}
                query={peopleEngagementQuery}
                searchState={peopleEngagementSearchState}
                onSearch={handlePeopleEngagementSearch}
              />
            </Col>
          </Row>

          <Row className="m-b-38">
            <Col xs={6}>
              <ImpactedPeopleWidget data={impactedPeople} />
            </Col>
            <Col xs={6}>
              <CITasksWidget data={changeImpactTasks} />
            </Col>
          </Row>

          <Row className="m-b-38">
            <Col xs={6}>
              <MilestonesWidget data={projectMilestones} />
            </Col>
            <Col xs={6}>
              <FeedbackIntelligenceWidget data={feedbackIntelligence} />
            </Col>
          </Row>

          <Row className="m-b-38">
            <Col xs={6}>
              <StakeholdersWidget
                data={stakeholdersOverview}
                projectId={projectId}
              />
            </Col>
            <Col xs={6}>
              <StakeholderSupportWidget data={stakeholderSupport} />
            </Col>
          </Row>
        </div>
      )}

      <DeleteModal
        onDelete={() => onConfirmDeleteProject()}
        show={deleteProjectState.showModal}
        onHide={() =>
          setDeleteProjectState({ showModal: false, projectId: null })
        }
        message={getOrgTermsContent('deleteProjectWithData')}
        title={getOrgTermsContent('deleteProject')}
      />
    </>
  );
}

ProjectDashboardContainer.propTypes = {
  projectId: PropTypes.number.isRequired,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ProjectDashboardContainer);
